import { Button, Group, LoadingOverlay, Table } from '@mantine/core';
import { IconPencilPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { BranchSelector } from '../../components/BranchSelector';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { SearchField } from '../../components/SearchField';
import { Title } from '../../components/Title';
import { ADMIN, BRISTEC_ADMIN } from '../../const/constants';
import { useAuth } from '../../hooks/useAuth';
import { useFilter } from '../../hooks/useFilter';
import { useModal } from '../../hooks/useModal';
import {
	type UsePaginationReturn,
	usePagination,
} from '../../hooks/usePagination';
import { useUrlParams } from '../../hooks/useUrlParams';
import { whiteIconButtonStyle } from '../../theme/styling/buttonStyling';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { query } from '../../util/api/query';
import { AccountRow } from './components/AccountRow';
import { NewAccountModal } from './components/NewAccountModal';
import type { AccountDto } from './types/AccountDto';
import { KeyedRow } from '../../components/KeyedRow';

export function Accounts() {
	const { organisation } = useParams();

	const pagination = usePagination();
	const filter = useFilter();

	const [isLoading, setIsLoading] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	const newModal = useModal();

	const { recordsParams, countParams } = useEndpointParams(
		pagination,
		searchQuery,
		filter.value.pharmacyGroupName ?? '',
	);

	const accountQuery = query<AccountDto[]>(
		`${organisation}/user/all?${recordsParams}`,
	);

	const accountCountQuery = query<number>(
		`${organisation}/user/count?${countParams}`,
	);

	const auth = useAuth();

	return (
		<>
			<NewAccountModal
				close={newModal.close}
				opened={newModal.isOpen}
				triggerListUpdate={() => {
					accountQuery.remove();
					accountQuery.remove();
					accountCountQuery.remove();
				}}
			/>
			<LoadingOverlay visible={isLoading} />
			<PageHeader title={'Accounts'} hideFiltering>
				<Group>
					<SearchField
						placeholder={'Search usernames...'}
						onDelayComplete={(value) => setSearchQuery(value)}
					/>
					<BranchSelector filter={filter} />
					<Button
						{...whiteIconButtonStyle}
						data-cy={'create-iconButton'}
						onClick={() => newModal.open()}
						disabled={
							![ADMIN, BRISTEC_ADMIN].includes(auth.getRole().toLowerCase())
						}
					>
						<IconPencilPlus {...defaultIconStyle} />
					</Button>
				</Group>
			</PageHeader>
			<DataTable
				pagination={pagination}
				totalItems={accountCountQuery.data?.value}
				isRowsLoading={accountQuery.isLoading}
				isPaginationLoading={accountCountQuery.isLoading}
				rows={pagination.value.pageSize}
			>
				<Table.Thead>
					<KeyedRow>
						<Table.Th>
							<Title label={'First Name'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Last Name'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Username'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Email'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Role'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Account Created'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Last Logged In'} />
						</Table.Th>
						<Table.Th />
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{accountQuery.data?.value.map((account) => (
						<AccountRow
							key={account.id}
							initialAccount={account}
							triggerListUpdate={() => {
								accountCountQuery.remove();
								accountQuery.remove();

								accountCountQuery.refetch();
								accountQuery.refetch();
							}}
							setIsLoading={setIsLoading}
							userRole={auth.getRole()}
						/>
					))}
				</Table.Tbody>
			</DataTable>
		</>
	);
}

function useEndpointParams(
	pagination: UsePaginationReturn,
	searchQuery: string,
	areaOrBranchName: string,
) {
	const recordsParams = useUrlParams({
		searchQuery: searchQuery,
		areaOrBranchName: areaOrBranchName,
		size: pagination.value.pageSize,
		number: pagination.value.pageNumber,
	});

	const countParams = useUrlParams({
		searchQuery: searchQuery,
		areaOrBranchName: areaOrBranchName,
	});

	return {
		recordsParams,
		countParams,
	};
}
